import TextValueEditor from './internal/editors/text/TextValueEditor.svelte';
import NumberValueEditor from './internal/editors/number/NumberValueEditor.svelte';
import ObjectValueEditor from './internal/meta-editors/object/ObjectValueEditor.svelte';
import type {
    ValueEditorConfigurationManagerProvider
} from './configuration/value-editor-configuration-manager.provider';
import type {ValueEditorLocalizationManagerProvider} from './localizations/value-editor-localization-manager.provider';
import ListValueEditor from './internal/meta-editors/list/ListValueEditor.svelte';
import type {ValueEditorRegistrationService} from './editor-type-registration/value-editor-registration.service';
import {
    TEXT_VALUE_EDITOR_DEFAULT_LOCALIZATIONS,
    TEXT_VALUE_EDITOR_DEFAULT_OPTIONS
} from './internal/editors/text/defaults';
import {
    LIST_VALUE_EDITOR_DEFAULT_LOCALIZATIONS,
    LIST_VALUE_EDITOR_DEFAULT_OPTIONS
} from './internal/meta-editors/list/defaults';
import {
    OBJECT_VALUE_EDITOR_DEFAULT_LOCALIZATIONS,
    OBJECT_VALUE_EDITOR_DEFAULT_OPTIONS
} from './internal/meta-editors/object/defaults';
import {
    NUMBER_VALUE_EDITOR_DEFAULT_LOCALIZATIONS,
    NUMBER_VALUE_EDITOR_DEFAULT_OPTIONS
} from './internal/editors/number/defaults';
import GenerableTextValueEditor from './internal/editors/generable-text/GenerableTextValueEditor.svelte';
import {
    GENERABLE_TEXT_VALUE_EDITOR_DEFAULT_LOCALIZATIONS,
    GENERABLE_TEXT_VALUE_EDITOR_DEFAULT_OPTIONS
} from './internal/editors/generable-text/defaults';
import {
    TEXT_OR_SELECT_VALUE_EDITOR_DEFAULT_LOCALIZATIONS,
    TEXT_OR_SELECT_VALUE_EDITOR_DEFAULT_OPTIONS
} from './internal/editors/text-or-select/defaults';
import TextOrSelectValueEditor from './internal/editors/text-or-select/TextOrSelectValueEditor.svelte';
import TextWithMetadataValueEditor from './internal/editors/text-with-metadata/TextWithMetadataValueEditor.svelte';
import {
    TEXT_WITH_METADATA_VALUE_EDITOR_DEFAULT_LOCALIZATIONS,
    TEXT_WITH_METADATA_VALUE_EDITOR_DEFAULT_OPTIONS
} from './internal/editors/text-with-metadata/defaults';
import ScannableTextValueEditor from './internal/editors/scannable-text/ScannableTextValueEditor.svelte';
import {
    SCANNABLE_TEXT_VALUE_EDITOR_DEFAULT_LOCALIZATIONS,
    SCANNABLE_TEXT_VALUE_EDITOR_DEFAULT_OPTIONS
} from './internal/editors/scannable-text/defaults';
import YearValueEditor from './internal/editors/year/YearValueEditor.svelte';
import {YEAR_VALUE_EDITOR_DEFAULT_LOCALIZATIONS} from './internal/editors/year/defaults';
import {
    DATE_VALUE_EDITOR_DEFAULT_LOCALIZATIONS,
    DATE_VALUE_EDITOR_DEFAULT_OPTIONS
} from './internal/editors/date/defaults';
import DateValueEditor from './internal/editors/date/DateValueEditor.svelte';
import MarkdownValueEditor from 'shared/value-editors/internal/editors/markdown/MarkdownValueEditor.svelte';
import {
    MARKDOWN_VALUE_EDITOR_DEFAULT_LOCALIZATIONS,
    MARKDOWN_VALUE_EDITOR_DEFAULT_OPTIONS
} from 'shared/value-editors/internal/editors/markdown/defaults';
import PasswordValueEditor from './internal/editors/password/PasswordValueEditor.svelte';
import {
    PASSWORD_VALUE_EDITOR_DEFAULT_LOCALIZATIONS,
    PASSWORD_VALUE_EDITOR_DEFAULT_OPTIONS
} from './internal/editors/password/defaults';
import BooleanValueEditor from './internal/editors/boolean/BooleanValueEditor.svelte';
import {
    BOOLEAN_VALUE_EDITOR_DEFAULT_LOCALIZATIONS,
    BOOLEAN_VALUE_EDITOR_DEFAULT_OPTIONS
} from './internal/editors/boolean/defaults';
import HiddenValueEditor from './internal/editors/hidden/HiddenValueEditor.svelte';
import RangeValueEditor from './internal/editors/range/RangeValueEditor.svelte';
import {
    RANGE_VALUE_EDITOR_DEFAULT_LOCALIZATIONS,
    RANGE_VALUE_EDITOR_DEFAULT_OPTIONS
} from './internal/editors/range/defaults';
import AutocompleteValueEditor from './internal/editors/autocomplete/AutocompleteValueEditor.svelte';
import {
    AUTOCOMPLETE_VALUE_EDITOR_DEFAULT_LOCALIZATIONS,
    AUTOCOMPLETE_VALUE_EDITOR_DEFAULT_OPTIONS
} from './internal/editors/autocomplete/defaults';
import SearchOrEditValueEditor from './internal/editors/search-or-edit/SearchOrEditValueEditor.svelte';
import {
    SEARCH_OR_EDIT_VALUE_EDITOR_DEFAULT_LOCALIZATIONS,
    SEARCH_OR_EDIT_VALUE_EDITOR_DEFAULT_OPTIONS
} from './internal/editors/search-or-edit/defaults';
import NumberRangeValueEditor from './internal/editors/number-range/NumberRangeValueEditor.svelte';
import {
    NUMBER_RANGE_VALUE_EDITOR_DEFAULT_LOCALIZATIONS,
    NUMBER_RANGE_VALUE_EDITOR_DEFAULT_OPTIONS
} from './internal/editors/number-range/defaults';
import SingleAcceptableValueEditor from './internal/editors/single-acceptable/SingleAcceptableValueEditor.svelte';
import {
    SINGLE_ACCEPTABLE_VALUE_EDITOR_DEFAULT_LOCALIZATIONS,
    SINGLE_ACCEPTABLE_VALUE_EDITOR_DEFAULT_OPTIONS
} from './internal/editors/single-acceptable/defaults';
import MultipleAcceptableValueEditor from './internal/editors/multiple-acceptable/MultipleAcceptableValueEditor.svelte';
import {
    MULTIPLE_ACCEPTABLE_VALUE_EDITOR_DEFAULT_LOCALIZATIONS,
    MULTIPLE_ACCEPTABLE_VALUE_EDITOR_DEFAULT_OPTIONS
} from './internal/editors/multiple-acceptable/defaults';
import AcceptableRootValueEditor from './internal/editors/acceptable-root/AcceptableRootValueEditor.svelte';
import {
    ACCEPTABLE_ROOT_VALUE_EDITOR_DEFAULT_LOCALIZATIONS,
    ACCEPTABLE_ROOT_VALUE_EDITOR_DEFAULT_OPTIONS
} from './internal/editors/acceptable-root/defaults';
import PriceValueEditor from './internal/editors/price/PriceValueEditor.svelte';
import {
    PRICE_VALUE_EDITOR_DEFAULT_LOCALIZATIONS,
    PRICE_VALUE_EDITOR_DEFAULT_OPTIONS
} from './internal/editors/price/defaults';
import {
    TELEPHONE_NUMBER_VALUE_EDITOR_DEFAULT_LOCALIZATIONS,
    TELEPHONE_NUMBER_VALUE_EDITOR_DEFAULT_OPTIONS
} from 'shared/value-editors/internal/editors/telephone-number/defaults';
import TelephoneNumberValueEditor
    from 'shared/value-editors/internal/editors/telephone-number/TelephoneNumberValueEditor.svelte';
import InlineRecordSearchValueEditor
    from 'src/features/record/inline-record-search/InlineRecordSearchValueEditor.svelte';
import {
    INLINE_RECORD_SEARCH_VALUE_EDITOR_DEFAULT_LOCALIZATIONS,
    INLINE_RECORD_SEARCH_VALUE_EDITOR_DEFAULT_OPTIONS
} from 'src/features/record/inline-record-search/defaults';
import DateRangeValueEditor
    from 'shared/value-editors/internal/editors/datetime-range/DateRangeValueEditor.svelte';
import {
    DATE_RANGE_VALUE_EDITOR_DEFAULT_LOCALIZATIONS,
    DATE_RANGE_VALUE_EDITOR_DEFAULT_OPTIONS
} from 'shared/value-editors/internal/editors/datetime-range/defaults';

/*@ngInject*/
export function valueEditorsRegistrationConfig(valueEditorRegistrationService: ValueEditorRegistrationService,
                                               valueEditorConfigurationManagerProvider: ValueEditorConfigurationManagerProvider,
                                               valueEditorLocalizationManagerProvider: ValueEditorLocalizationManagerProvider): void {

    valueEditorRegistrationService
        .registerEditorComponent('text', TextValueEditor)
        .registerEditorComponent('number', NumberValueEditor)
        .registerEditorComponent('object', ObjectValueEditor)
        .registerEditorComponent('list', ListValueEditor)
        .registerEditorComponent('generable-text', GenerableTextValueEditor)
        .registerEditorComponent('text-or-select', TextOrSelectValueEditor)
        .registerEditorComponent('text-with-metadata', TextWithMetadataValueEditor)
        .registerEditorComponent('scannable-text', ScannableTextValueEditor)
        .registerEditorComponent('year', YearValueEditor)
        .registerEditorComponent('date', DateValueEditor)
        .registerEditorComponent('markdown', MarkdownValueEditor)
        .registerEditorComponent('password', PasswordValueEditor)
        .registerEditorComponent('boolean', BooleanValueEditor)
        .registerEditorComponent('hidden', HiddenValueEditor)
        .registerEditorComponent('range', RangeValueEditor)
        .registerEditorComponent('autocomplete', AutocompleteValueEditor)
        .registerEditorComponent('search-or-edit', SearchOrEditValueEditor)
        .registerEditorComponent('number-range', NumberRangeValueEditor)
        .registerEditorComponent('single-acceptable', SingleAcceptableValueEditor)
        .registerEditorComponent('multiple-acceptable', MultipleAcceptableValueEditor)
        .registerEditorComponent('acceptable-root', AcceptableRootValueEditor)
        .registerEditorComponent('price', PriceValueEditor)
        .registerEditorComponent('telephone-number', TelephoneNumberValueEditor)
        .registerEditorComponent('inline-record-search', InlineRecordSearchValueEditor)
        .registerEditorComponent('date-range', DateRangeValueEditor);

    valueEditorConfigurationManagerProvider
        .registerForType('text', TEXT_VALUE_EDITOR_DEFAULT_OPTIONS)
        .registerForType('number', NUMBER_VALUE_EDITOR_DEFAULT_OPTIONS)
        .registerForType('object', OBJECT_VALUE_EDITOR_DEFAULT_OPTIONS)
        .registerForType('list', LIST_VALUE_EDITOR_DEFAULT_OPTIONS)
        .registerForType('generable-text', GENERABLE_TEXT_VALUE_EDITOR_DEFAULT_OPTIONS)
        .registerForType('text-or-select', TEXT_OR_SELECT_VALUE_EDITOR_DEFAULT_OPTIONS)
        .registerForType('text-with-metadata', TEXT_WITH_METADATA_VALUE_EDITOR_DEFAULT_OPTIONS)
        .registerForType('scannable-text', SCANNABLE_TEXT_VALUE_EDITOR_DEFAULT_OPTIONS)
        .registerForType('year', {})
        .registerForType('date', DATE_VALUE_EDITOR_DEFAULT_OPTIONS)
        .registerForType('markdown', MARKDOWN_VALUE_EDITOR_DEFAULT_OPTIONS)
        .registerForType('password', PASSWORD_VALUE_EDITOR_DEFAULT_OPTIONS)
        .registerForType('boolean', BOOLEAN_VALUE_EDITOR_DEFAULT_OPTIONS)
        .registerForType('hidden', {})
        .registerForType('range', RANGE_VALUE_EDITOR_DEFAULT_OPTIONS)
        .registerForType('autocomplete', AUTOCOMPLETE_VALUE_EDITOR_DEFAULT_OPTIONS)
        .registerForType('search-or-edit', SEARCH_OR_EDIT_VALUE_EDITOR_DEFAULT_OPTIONS)
        .registerForType('number-range', NUMBER_RANGE_VALUE_EDITOR_DEFAULT_OPTIONS)
        .registerForType('single-acceptable', SINGLE_ACCEPTABLE_VALUE_EDITOR_DEFAULT_OPTIONS)
        .registerForType('multiple-acceptable', MULTIPLE_ACCEPTABLE_VALUE_EDITOR_DEFAULT_OPTIONS)
        .registerForType('acceptable-root', ACCEPTABLE_ROOT_VALUE_EDITOR_DEFAULT_OPTIONS)
        .registerForType('price', PRICE_VALUE_EDITOR_DEFAULT_OPTIONS)
        .registerForType('telephone-number', TELEPHONE_NUMBER_VALUE_EDITOR_DEFAULT_OPTIONS)
        .registerForType('inline-record-search', INLINE_RECORD_SEARCH_VALUE_EDITOR_DEFAULT_OPTIONS)
        .registerForType('date-range', DATE_RANGE_VALUE_EDITOR_DEFAULT_OPTIONS);

    valueEditorLocalizationManagerProvider
        .registerForType('text', TEXT_VALUE_EDITOR_DEFAULT_LOCALIZATIONS)
        .registerForType('number', NUMBER_VALUE_EDITOR_DEFAULT_LOCALIZATIONS)
        .registerForType('list', LIST_VALUE_EDITOR_DEFAULT_LOCALIZATIONS)
        .registerForType('object', OBJECT_VALUE_EDITOR_DEFAULT_LOCALIZATIONS)
        .registerForType('generable-text', GENERABLE_TEXT_VALUE_EDITOR_DEFAULT_LOCALIZATIONS)
        .registerForType('text-or-select', TEXT_OR_SELECT_VALUE_EDITOR_DEFAULT_LOCALIZATIONS)
        .registerForType('text-with-metadata', TEXT_WITH_METADATA_VALUE_EDITOR_DEFAULT_LOCALIZATIONS)
        .registerForType('scannable-text', SCANNABLE_TEXT_VALUE_EDITOR_DEFAULT_LOCALIZATIONS)
        .registerForType('year', YEAR_VALUE_EDITOR_DEFAULT_LOCALIZATIONS)
        .registerForType('date', DATE_VALUE_EDITOR_DEFAULT_LOCALIZATIONS)
        .registerForType('markdown', MARKDOWN_VALUE_EDITOR_DEFAULT_LOCALIZATIONS)
        .registerForType('password', PASSWORD_VALUE_EDITOR_DEFAULT_LOCALIZATIONS)
        .registerForType('boolean', BOOLEAN_VALUE_EDITOR_DEFAULT_LOCALIZATIONS)
        .registerForType('hidden', {})
        .registerForType('range', RANGE_VALUE_EDITOR_DEFAULT_LOCALIZATIONS)
        .registerForType('autocomplete', AUTOCOMPLETE_VALUE_EDITOR_DEFAULT_LOCALIZATIONS)
        .registerForType('search-or-edit', SEARCH_OR_EDIT_VALUE_EDITOR_DEFAULT_LOCALIZATIONS)
        .registerForType('number-range', NUMBER_RANGE_VALUE_EDITOR_DEFAULT_LOCALIZATIONS)
        .registerForType('single-acceptable', SINGLE_ACCEPTABLE_VALUE_EDITOR_DEFAULT_LOCALIZATIONS)
        .registerForType('multiple-acceptable', MULTIPLE_ACCEPTABLE_VALUE_EDITOR_DEFAULT_LOCALIZATIONS)
        .registerForType('acceptable-root', ACCEPTABLE_ROOT_VALUE_EDITOR_DEFAULT_LOCALIZATIONS)
        .registerForType('price', PRICE_VALUE_EDITOR_DEFAULT_LOCALIZATIONS)
        .registerForType('telephone-number', TELEPHONE_NUMBER_VALUE_EDITOR_DEFAULT_LOCALIZATIONS)
        .registerForType('inline-record-search', INLINE_RECORD_SEARCH_VALUE_EDITOR_DEFAULT_LOCALIZATIONS)
        .registerForType('date-range', DATE_RANGE_VALUE_EDITOR_DEFAULT_LOCALIZATIONS);
}